import clsx from 'clsx';
import Link from 'next/link';
import { CSSProperties, useCallback, useMemo } from 'react';

import { Icon } from 'common/icon';
import { ActionBlockButton } from 'components/action-block';

import { ICasino } from 'constants/types';

import { BlurHashImage } from 'components/blurhash-image';
import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { LoginModal } from 'components/modal/login';
import { Anchor } from 'constants/anchors';
import { RTPI_UTM, Routes } from 'constants/routes';
import { useLoginModal, useRedirectActions } from 'hooks';
import { useFavorites } from 'hooks/useFavorites';
import { useRTPWeights } from 'hooks/useRTPWeights';
import { useMeQuery } from 'hooks/api/users';
import { useAuth } from 'context/auth';
import { useRegion } from 'context/region';
import { formatPluralWord } from 'utils/formats';
import { getItemIdentifier } from 'utils/slug';

import { Tag } from 'components/slider/cards/medium/tag';
import { StarRating } from 'components/star-rating';
import { colors } from 'components/template/streamers/config';
import { Analytics } from 'constants/analytics';
import { RedirectActions } from 'constants/redirect-actions';
import { getCasinoRTP } from 'utils/casino';
import { saveRedirectOptions } from 'utils/redirectOptions';
import { getColor } from 'utils/tagColor';
import styles from './styles.module.css';
import { RegionsModal } from 'components/modal/regions';

const UnvailableMark = () => {
  return (
    <div className={styles.unavailable_container}>
      <div className={styles.unavailable_content}>
        <div className={styles.unavailable_icon}>
          <Icon.Close width={20} height={20} />
        </div>
        <div className={styles.unvailable_text}>
          Not available in your region
        </div>
      </div>
      <div className={styles.regions_modal_container}>
        <RegionsModal textClassName={styles.regions_text} />
      </div>
    </div>
  );
};

const CasinoAlert = ({
  text,
  isClosed,
  className,
}: {
  text: string;
  isClosed?: boolean;
  classAlert?: string | undefined;
  className?: string;
}) => {
  return (
    <div className={clsx(styles.alert, className)}>
      {isClosed ? <Icon.Close /> : <Icon.ExclamationMark fill={'white'} />}
      <span>{isClosed ? 'Casino closed down' : text}</span>
    </div>
  );
};

export const CasinoHeader = ({ data }: { data: ICasino }) => {
  const { region: currentRegion } = useRegion();
  const { isLoggedIn } = useAuth();

  const isCasinoAvailable =
    currentRegion === null ||
    currentRegion === 'null' ||
    currentRegion === undefined
      ? true
      : !!data?.countries_ids?.find((item) => item === currentRegion);

  const { data: { user } = {}, isFetching: isUserFetching } = useMeQuery();
  const { addToFavorite, removeFromFavorite, isLoading } = useFavorites();
  const linkChooseCasino = Routes.blogAndNewsPage(
    'how-to-choose-a-casino-based-on-rtpcheck-a-complete-guide'
  );

  const RTP = getCasinoRTP(data).number?.toFixed(2);
  const starRating = data?.rating;

  const {
    isBad,
    isGood,
    RTPHigh,
    isFetching: isRatingFetching,
  } = useRTPWeights();

  const isClosed = data?.isClosed;
  const renderIcon = useCallback(() => {
    if (isRatingFetching) {
      return <Icon.Cherry width="60" height="60" fill="#FEFEFE" />;
    }
    if (isBad(parseInt(RTP))) {
      return <Icon.Bad width="60" height="60" />;
    } else if (isGood(parseInt(RTP))) {
      return <Icon.Good width="60" height="60" />;
    } else {
      return <Icon.Cherry width="60" height="60" />;
    }
  }, [isBad, isGood, isRatingFetching, RTP]);

  const isFavorite = useMemo(() => {
    if (
      user?.favorite_casinos?.find((item) => item._id === data?._id) &&
      user?.favorite_casinos?.length
    ) {
      return true;
    }

    return false;
  }, [data?._id, user]);

  const onFavoriteText = useMemo(() => {
    if (isLoading || isUserFetching) {
      return 'Loading...';
    }
    if (isFavorite) {
      return 'Remove';
    } else {
      return 'Subscribe';
    }
  }, [isFavorite, isLoading, isUserFetching]);

  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();

  useRedirectActions({
    actions: [
      {
        name: RedirectActions.Favorite,
        action: () => addToFavorite({ casinoId: data?._id }),
      },
    ],
  });
  const dynamicStyle = useMemo(() => {
    if (isRatingFetching) {
      return {
        rtpiStatus: 'High RTP. You can win',
        textAlert: 'alert: high RTPi',
        classContainer: isClosed
          ? styles.casino_closed
          : styles.casino_rating_hight,
      };
    }
    if (isBad(parseInt(RTP))) {
      return {
        rtpiStatus: 'Very low RTP, impossible to win!',
        textAlert: 'VERY LOW RTP',
        textWarning: 'This casino has a very low return rate',
        classContainer: isClosed
          ? styles.casino_closed
          : styles.casino_rating_low,
      };
    }
    if (isGood(parseInt(RTP))) {
      return {
        rtpiStatus: 'Low RTP, casino reduces!',
        textAlert: 'REDUCE RTP',
        textWarning:
          'This casino reduces the percentage of return, we do not recommend playing here, as the probability of winning is lower than it should be.',
        classContainer: isClosed
          ? styles.casino_closed
          : styles.casino_rating_medium,
      };
    }

    return {
      rtpiStatus: 'High RTP. You can win',
      textAlert: 'alert: high RTPi',
      classContainer: isClosed
        ? styles.casino_closed
        : styles.casino_rating_hight,
    };
  }, [RTP, isRatingFetching, isClosed, isBad, isGood]);

  const backgroundStyle: CSSProperties = {
    background: `url(${data?.imageUrl}) top/cover no-repeat`,
  };

  return (
    <div
      className={clsx(
        styles.container,
        dynamicStyle.classContainer,
        !isCasinoAvailable && styles.casino_unavailable
      )}
    >
      <div className={styles.container_background} style={backgroundStyle} />
      <div className={styles.container_s}>
        <div className={styles.casino_block__row}>
          <div className={styles.casino_block__card}>
            <div className={styles.casino_block__card_wrapper}>
              {data?.tags && (
                <div className={styles.tags}>
                  {data.tags.map((tag, index) => (
                    <Tag
                      color={getColor(tag, colors)}
                      text={tag}
                      key={tag + index}
                      className={styles.userTag}
                    />
                  ))}
                </div>
              )}
              <LogoOverlayWrapper>
                <BlurHashImage
                  alt={''}
                  blurhash={data?.blurHash}
                  src={data?.imageUrl ?? ''}
                  objectFit="contain"
                  className={styles.casino_card_image}
                />

                <LogoOverlay alt={data?.name} image={data?.logoUrl} />
                <CasinoAlert
                  isClosed={isClosed}
                  text={dynamicStyle.textAlert}
                  className={isClosed ? styles.low_rtp_alert_base : ''}
                />
              </LogoOverlayWrapper>
              {!isCasinoAvailable && <UnvailableMark />}
              {isCasinoAvailable &&
                !isClosed &&
                (parseInt(RTP) >= RTPHigh ? (
                  <a
                    id={Analytics.casino.play}
                    target="_blank"
                    href={data.link_play ?? `${data?.website}${RTPI_UTM}`}
                    className={styles.casino_play_button}
                  >
                    <Icon.Play
                      width={'30'}
                      height={'30'}
                      fill="#121212"
                      backgroundFill="#caf313"
                    />
                    <span className={styles.casino_play_button_text}>
                      Play casino
                    </span>
                  </a>
                ) : (
                  <a
                    id={Analytics.casino.play}
                    target="_blank"
                    href={Routes.allCasinos}
                    className={clsx(
                      styles.casino_play_button,
                      styles.casino_play_button_text_fullwidth,
                      styles.casino_choose_another_casino_button
                    )}
                  >
                    <span className={clsx(styles.casino_play_button_text)}>
                      Choose another casino
                    </span>
                    <Icon.Arrow width={20} height={20} />
                  </a>
                ))}
            </div>
          </div>
          <div className={styles.casino_block__info}>
            <div className={styles.casino_rating}>
              <StarRating
                ratingNumber={starRating?.toString()}
                color={'white'}
                starSize="14"
                starGap="5px"
              />
              {parseFloat(starRating)?.toFixed(2)}
              <a
                id={Analytics.casino.anchorRtpReviews}
                href={`#${Anchor.slot.reviews}`}
                className={styles.casino_review_info_text}
              >
                <span>{`${'based on'} ${data?.slots_reviews_count} ${'reviews'}`}</span>
                <Icon.Arrow width={20} height={20} />
              </a>
            </div>
            <div className={styles.casino__title}>
              <h1>
                {data?.name}:
                <br />
                {'RTP casino & review'}
              </h1>
            </div>
            <ActionBlockButton
              data={[
                {
                  id: isFavorite
                    ? Analytics.casino.unsave
                    : Analytics.casino.save,
                  anchorId: 0,
                  text: onFavoriteText,
                  icon: isFavorite ? 'remove' : 'save',
                  onClick: async () => {
                    if (isLoggedIn) {
                      if (isFavorite) {
                        await removeFromFavorite({ favoriteId: data?._id });
                      } else {
                        await addToFavorite({ casinoId: data?._id });
                      }
                    } else {
                      saveRedirectOptions({
                        url: Routes.casinoPage(getItemIdentifier(data)),
                        action: RedirectActions.Favorite,
                      });
                      handleVisibleLoginModal();
                    }
                  },
                },
                {
                  id: Analytics.casino.inspect,
                  anchorId: Anchor.Inspect,
                  text: 'Add review',
                  icon: 'inspect',
                },
                {
                  id: Analytics.casino.allInspections,
                  anchorId: Anchor.CasinoReviews,
                  text: formatPluralWord({
                    number:
                      data?.casinos_reviews_count + data?.slots_reviews_count,
                    word: 'check',
                  }),
                  icon: 'search',
                },
              ]}
            />
            <div className={styles.casino_rtpi_container}>
              <div className={styles.casino_rtpi_container_row}>
                <div className={styles.casino_rtpi_icon}>{renderIcon()}</div>
                <div className={styles.casino_rtpi_rating_container}>
                  <span className={styles.casino_rtpi_status_text}>
                    {dynamicStyle.rtpiStatus}
                  </span>
                  <span className={styles.casino_rtpi_count_text}>{RTP}%</span>
                </div>
              </div>
            </div>

            <div>
              <a
                id={Analytics.casino.anchorRtpReviews}
                href={`#${Anchor.slot.reviews}`}
                className={styles.casino_review_info_text}
              >
                <span>{`${'based on'} ${data?.slots_reviews_count} ${'slot RTP reviews'}`}</span>
                <Icon.Arrow width={20} height={20} />
              </a>
              <Link
                id={Analytics.casino.howToChoose}
                href={linkChooseCasino}
                className={clsx(styles.casino_how_to_choose_banner_container)}
              >
                <Icon.ReadPost />
                <span className={styles.casino_how_to_choose_banner_text}>
                  {'How to choose a casino by RTP?'}
                </span>
                <Icon.Arrow width={20} height={20} />
              </Link>
            </div>
          </div>
        </div>
        {parseInt(RTP) < RTPHigh && (
          <div className={styles.warning_container}>
            <div className={styles.warning_wrapper}>
              <div className={styles.warning_icon}>
                <Icon.Warning />
              </div>
              {dynamicStyle.textWarning && <p>{dynamicStyle.textWarning}</p>}
            </div>
          </div>
        )}
      </div>

      <LoginModal
        isVisible={isLoginVisible}
        onClose={handleCloseLoginModal}
        confirmOnClick={handleConfirmLoginModal}
      />
    </div>
  );
};
